import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import Cookies from 'js-cookie'
import type { Store, Register, Staff } from '@/types'
import { useUIStore } from '@/stores/uiStore'

const STORE_COOKIE_NAME = 'waio_store_id'
const REGISTER_COOKIE_NAME = 'waio_register_id'

// Persisted-store keys we own. Cleared explicitly on logout in addition to
// the in-memory reset, so a browser crash mid-write or a missed onCartClear
// callback can't leave PII (cart customer, lines) behind for the next user.
const SESSION_STORAGE_KEY = 'waio-pos-session'
const CART_STORAGE_KEYS = ['waio-pos-v2-cart', 'waio-pos-cart'] as const

export function clearPersistedCart() {
  if (typeof window === 'undefined') return
  for (const key of CART_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Storage access blocked (private mode, quota, etc.) — nothing to do.
    }
  }
}

function clearPersistedSession() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY)
  } catch {
    // Storage access blocked — nothing to do.
  }
}

interface SessionState {
  backendUrl: string | null

  // JWT tokens
  authType: 'manager' | 'store' | null
  sessionToken: string | null
  refreshToken: string | null
  clerkToken: string | null

  // Identifier returned with the auth token. For manager auth this is
  // the email used to log in; for store/API-key auth the BE may return
  // a derived label or omit it entirely. Surfaced in the
  // /select-store and /select-register screens so the cashier sees
  // who's signed in before they pick a store/register.
  authUsername: string | null

  // Manager-auth interim state (in-memory only — never persisted).
  // `userToken` is short-lived (60 min) and only valid for
  // /v2/auth/select-store + /v2/auth/stores; we exchange it for a real
  // session_token via select-store, then discard. `accessibleStores` is
  // the list returned with the manager login response, used by the
  // /select-store picker.
  userToken: string | null
  accessibleStores: Store[] | null

  // Staff info (set after clerk PIN login)
  staff: Staff | null
  isAuthenticated: boolean

  // Store/Register selection
  selectedStore: Store | null
  selectedRegister: Register | null

  // Clock status
  isClockedIn: boolean
  clockedInAt: string | null

  // Resolved company logo URL for the customer-facing display. Written
  // by auth hooks from /v2/auth/* responses (store-tier from /token and
  // /select-store; full-hierarchy from /clerk) so the CFD can paint a
  // brand logo BEFORE /pos/bootstrap completes. Persisted so a CFD
  // reload mid-session shows the cached logo instantly while the
  // bootstrap query reconfirms in the background.
  displayLogo: string | null
}

interface SessionActions {
  setBackendUrl: (url: string) => void

  // JWT auth actions
  setAuth: (sessionToken: string, refreshToken: string, authType: 'manager' | 'store', store: Store, username?: string | null) => void
  // Manager-flow interim: stash the user_token + accessible stores so
  // the /select-store route can render the picker and call
  // /v2/auth/select-store. Cleared after a successful pick (setAuth)
  // or on logout.
  setUserToken: (userToken: string, accessibleStores: Store[], username?: string | null) => void
  clearUserToken: () => void
  // Refresh the manager's accessible-store list with the latest BE
  // payload — call after /v2/auth/stores succeeds so a reload starts
  // from current data instead of last-login data.
  setAccessibleStores: (stores: Store[]) => void
  // After token refresh — update session + refresh tokens, and
  // optionally a rebound clerk_token. The BE's /v2/auth/refresh
  // endpoint re-mints the clerk_token bound to the new session JTI
  // whenever we send the old one in the request body; passing it here
  // keeps the FE store in sync so the next request doesn't hit a
  // session_jti mismatch (BE jwt_auth.py L342). Omit/undefined leaves
  // clerkToken unchanged for callers that don't have one (manager-only
  // flows, pre-PIN refreshes).
  setSessionTokens: (sessionToken: string, refreshToken: string, clerkToken?: string) => void
  setClerkAuth: (clerkToken: string, staff: Staff, register: Register) => void
  clearClerkAuth: () => void
  invalidateClerkToken: () => void
  clearClerkKeepRegister: () => void

  // Staff actions
  setStaff: (staff: Staff) => void
  clearStaff: () => void

  // Store selection (also sets cookie for backend)
  setStore: (store: Store) => void

  // Register selection (also sets cookie for backend)
  setRegister: (register: Register, onCartClear?: () => void) => void

  // Clock in/out
  clockIn: () => void
  clockOut: () => void

  // Full session clear (logout)
  clearSession: (onCartClear?: () => void) => void

  // Check if register cookie exists
  hasRegisterCookie: () => boolean

  // Set the display logo URL. Called by auth hooks from /v2/auth/*
  // responses; kept separate from setAuth so each auth event is a
  // single, traceable write.
  setDisplayLogo: (url: string | null) => void
}

type SessionStore = SessionState & SessionActions

// Hydration promise - resolves when Zustand persist finishes loading from localStorage
let hydrationResolve: (() => void) | null = null
const hydrationPromise = new Promise<void>((resolve) => {
  hydrationResolve = resolve
})

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      // Initial state
      backendUrl: null,
      authType: null,
      sessionToken: null,
      refreshToken: null,
      clerkToken: null,
      authUsername: null,
      userToken: null,
      accessibleStores: null,
      staff: null,
      isAuthenticated: false,
      selectedStore: null,
      selectedRegister: null,
      isClockedIn: false,
      clockedInAt: null,
      displayLogo: null,

      setBackendUrl: (url) => {
        set({ backendUrl: url })
      },

      // After manager/store login — stores JWT tokens + store info.
      // Drops the short-lived user_token (it's already been spent on
      // /v2/auth/select-store) but keeps `accessibleStores` so a
      // multi-store manager can come back to /select-store and switch
      // without re-authenticating.
      setAuth: (sessionToken, refreshToken, authType, store, username) => {
        set({
          sessionToken,
          refreshToken,
          authType,
          selectedStore: store,
          authUsername: username ?? null,
          userToken: null,
        })
      },

      // Manager-flow stash. Wipes any prior session tokens so a leftover
      // sessionToken from a previous login doesn't override the
      // user_token Authorization header on the upcoming /select-store
      // call (the apiClient interceptor reads sessionToken for
      // /v2/auth/* endpoints).
      setUserToken: (userToken, accessibleStores, username) => {
        set({
          sessionToken: null,
          refreshToken: null,
          authType: 'manager',
          userToken,
          accessibleStores,
          authUsername: username ?? null,
          selectedStore: null,
        })
      },

      clearUserToken: () => {
        set({ userToken: null, accessibleStores: null })
      },

      setAccessibleStores: (stores) => {
        set({ accessibleStores: stores })
      },

      // After token refresh — update tokens only. clerkToken is
      // optional: when /v2/auth/refresh re-issues a clerk_token (we
      // sent the old one in the body), forward it here so the store
      // stays in sync. Otherwise leave clerkToken untouched.
      setSessionTokens: (sessionToken, refreshToken, clerkToken) => {
        set({
          sessionToken,
          refreshToken,
          ...(clerkToken !== undefined ? { clerkToken } : {}),
        })
      },

      // After clerk PIN login — stores clerk token + staff + register
      setClerkAuth: (clerkToken, staff, register) => {
        set({
          clerkToken,
          staff,
          isAuthenticated: true,
          selectedRegister: register,
        })
      },

      // Clerk-session recovery (client.ts recoverDeadClerkSession):
      // mark the clerk credential dead WITHOUT tearing down the register
      // context. Only clerkToken drops — staff stays so the re-PIN
      // overlay can show who needs to sign in; selectedRegister and
      // isAuthenticated stay so the /pos shell (and its route guards)
      // keep the screen alive underneath the overlay.
      invalidateClerkToken: () => {
        set({ clerkToken: null })
      },

      // The re-PIN overlay's Logout escape: clear the clerk like
      // clearClerkAuth but KEEP selectedRegister, so navigating to
      // /login lands directly on the PIN page (clearClerkAuth's
      // register-nulling would bounce /login → /select-register).
      clearClerkKeepRegister: () => {
        set({
          clerkToken: null,
          staff: null,
          isAuthenticated: false,
          isClockedIn: false,
          clockedInAt: null,
        })
        clearPersistedCart()
        useUIStore.getState().closeCashModals()
      },

      // Clerk logout — drop clerk token but keep session
      clearClerkAuth: () => {
        set({
          clerkToken: null,
          staff: null,
          isAuthenticated: false,
          selectedRegister: null,
          isClockedIn: false,
          clockedInAt: null,
        })
        clearPersistedCart()
        // The cash-modal open flags are in-memory and not part of the
        // session state cleared above, so a Cash Out left open by this
        // clerk would survive into the next clerk's session on terminals
        // that don't hard-reload. Close them here as the session tears
        // down (covers inactivity logout, clerk logout, and switch-store);
        // useClerkLogin resets them again on the next login.
        useUIStore.getState().closeCashModals()
      },

      // Staff actions
      setStaff: (staff: Staff) => {
        set({ staff, isAuthenticated: true })
      },

      clearStaff: () => {
        set({ staff: null, isAuthenticated: false })
      },

      // Store selection with cookie
      setStore: (store: Store) => {
        Cookies.set(STORE_COOKIE_NAME, String(store.id), {
          path: '/',
          expires: 365,
        })
        set({ selectedStore: store })
      },

      // Register selection with cart clearing on store change and cookie update
      setRegister: (register, onCartClear) => {
        const currentStore = get().selectedStore
        if (currentStore && currentStore.id !== register.store_id) {
          onCartClear?.()
        }

        // Set cookies for backend API calls
        Cookies.set(REGISTER_COOKIE_NAME, String(register.id), {
          path: '/',
          expires: 365,
        })
        Cookies.set(STORE_COOKIE_NAME, String(register.store_id), {
          path: '/',
          expires: 365,
        })

        set({
          selectedRegister: register,
          selectedStore: currentStore?.id === register.store_id ? currentStore : {
            id: register.store_id,
            name: register.store_name || `Store ${register.store_id}`,
            address: null,
            city: null,
            province: null,
            postal_code: null,
            phone: null,
            timezone: 'America/Toronto',
            is_cannabis: true,
            default_tax_rate: 13,
          },
        })
      },

      // Clock in/out
      clockIn: () => {
        set({ isClockedIn: true, clockedInAt: new Date().toISOString() })
      },

      clockOut: () => {
        set({ isClockedIn: false, clockedInAt: null })
      },

      // Full logout — clear everything except backendUrl
      clearSession: (onCartClear) => {
        onCartClear?.()

        // Remove cookies
        Cookies.remove(REGISTER_COOKIE_NAME, { path: '/' })
        Cookies.remove(STORE_COOKIE_NAME, { path: '/' })

        set({
          authType: null,
          sessionToken: null,
          refreshToken: null,
          clerkToken: null,
          authUsername: null,
          userToken: null,
          accessibleStores: null,
          staff: null,
          isAuthenticated: false,
          selectedStore: null,
          selectedRegister: null,
          isClockedIn: false,
          clockedInAt: null,
          displayLogo: null,
        })

        clearPersistedCart()
        clearPersistedSession()
      },

      // Check register cookie
      hasRegisterCookie: () => {
        return !!Cookies.get(REGISTER_COOKIE_NAME)
      },

      setDisplayLogo: (url) => {
        set({ displayLogo: url })
      },
    }),
    {
      name: 'waio-pos-session',
      partialize: (state) => ({
        backendUrl: state.backendUrl,
        authType: state.authType,
        sessionToken: state.sessionToken,
        refreshToken: state.refreshToken,
        clerkToken: state.clerkToken,
        authUsername: state.authUsername,
        accessibleStores: state.accessibleStores,
        staff: state.staff,
        isAuthenticated: state.isAuthenticated,
        selectedStore: state.selectedStore,
        selectedRegister: state.selectedRegister,
        isClockedIn: state.isClockedIn,
        clockedInAt: state.clockedInAt,
        displayLogo: state.displayLogo,
      }),
      onRehydrateStorage: () => {
        return () => {
          hydrationResolve?.()
        }
      },
    }
  )
)

/**
 * Wait for Zustand persist middleware to finish hydrating from localStorage.
 * Must be awaited in async `beforeLoad` route guards before reading store state.
 */
export function waitForHydration(): Promise<void> {
  return hydrationPromise
}
